<template>
  <div v-if="!collapse" class="locale-theme-row">
    <el-dropdown
      placement="top"
      @command="changeLocale"
    >
      <el-button id="btn-language-selector" text class="locale-button">
        <span class="locale-content">
          <span class="locale-flag">{{ getLocaleFlag(locale) }}</span>
          <span class="locale-text">{{ currentLocaleLabel }}</span>
        </span>
        <el-icon class="el-icon--right">
          <ArrowDown />
        </el-icon>
      </el-button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
            v-for="option in optionsLocales"
            :key="option.value"
            :id="`item-language-${option.value}`"
            :command="option.value"
            :class="{ 'is-active': option.value === locale }"
          >
            {{ option.label }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <el-tooltip :content="isDark ? t('theme.light') : t('theme.dark')" placement="top">
      <el-button text class="theme-toggle-button" @click="toggleTheme">
        <el-icon :size="16">
          <Sunny v-if="isDark" />
          <Moon v-else />
        </el-icon>
      </el-button>
    </el-tooltip>
  </div>

  <div v-else class="locale-theme-col">
    <el-tooltip :content="currentLocaleLabel" placement="right">
      <el-dropdown placement="right" @command="changeLocale">
        <el-button id="btn-language-selector-collapsed" text class="locale-button-collapsed">
          <span class="locale-flag">{{ getLocaleFlag(locale) }}</span>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item
              v-for="option in optionsLocales"
              :key="option.value"
              :command="option.value"
              :class="{ 'is-active': option.value === locale }"
            >
              {{ option.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </el-tooltip>

    <el-tooltip :content="isDark ? t('theme.light') : t('theme.dark')" placement="right">
      <el-button text class="theme-toggle-collapsed" @click="toggleTheme">
        <el-icon :size="16">
          <Sunny v-if="isDark" />
          <Moon v-else />
        </el-icon>
      </el-button>
    </el-tooltip>
  </div>
</template>

<script setup lang="ts">
import {ElDropdown, ElDropdownMenu, ElDropdownItem, ElButton, ElIcon, ElTooltip} from 'element-plus'
import {ArrowDown, Sunny, Moon} from '@element-plus/icons-vue'
import {useLocale} from '@/composables/locale'
import {useTheme} from '@/composables/theme'
import {useI18n} from 'vue-i18n'
import {computed} from 'vue'

defineProps<{collapse?: boolean}>()

const {locale, optionsLocales, changeLocale} = useLocale()
const {isDark, toggleTheme} = useTheme()
const {t} = useI18n()

const currentLocaleLabel = computed(() => {
  return optionsLocales.find(option => option.value === locale.value)?.label || ''
})

const getLocaleFlag = (localeValue: string) => {
  const flags: Record<string, string> = {
    'ru': '🇷🇺',
    'kk': '🇰🇿',
  }
  return flags[localeValue] || '🌐'
}
</script>

<style scoped>
.locale-theme-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.locale-theme-row .el-dropdown {
  flex: 1;
  min-width: 0;
}

.locale-theme-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.locale-button {
  color: var(--sidebar-text-color) !important;
  font-weight: 500;
  padding: 8px 12px;
  width: 100%;
  justify-content: space-between;
  border-radius: 6px;
  border: 1px solid var(--sidebar-border-color) !important;
  transition: all 0.2s;

  &:hover {
    background-color: var(--sidebar-hover-bg);
    border-color: var(--sidebar-hover-border) !important;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }

  .locale-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .locale-flag {
    font-size: 16px;
    line-height: 1;
  }

  .locale-text {
    font-size: 14px;
  }
}

.locale-button-collapsed {
  color: var(--sidebar-text-color) !important;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid var(--sidebar-border-color) !important;
  transition: all 0.2s;

  &:hover {
    background-color: var(--sidebar-hover-bg);
    border-color: var(--sidebar-hover-border) !important;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }

  .locale-flag {
    font-size: 16px;
    line-height: 1;
  }
}

.theme-toggle-button {
  color: var(--sidebar-text-color) !important;
  width: 34px;
  height: 34px;
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid var(--sidebar-border-color) !important;
  transition: all 0.2s;

  &:hover {
    background-color: var(--sidebar-hover-bg);
    border-color: var(--sidebar-hover-border) !important;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
}

.theme-toggle-collapsed {
  color: var(--sidebar-text-color) !important;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid var(--sidebar-border-color) !important;
  transition: all 0.2s;

  &:hover {
    background-color: var(--sidebar-hover-bg);
    border-color: var(--sidebar-hover-border) !important;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
}

.is-active {
  background-color: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
}
</style>
